// Editors enter publication dates in many shapes ("2026-02-06", "21 Nov 2025",
// "12/12/2025", "10/2025"). Rendering those verbatim made a single listing show
// four different date styles side by side. This helper parses the common shapes
// and renders one consistent French date; anything it can't recognise as a date
// is returned untouched (data-record metas, free text, already-formatted strings).
//
// It mirrors the admin's `TryParseSortableDate` (SqlContentRepository.cs): the
// day-first reading wins for an ambiguous "01/05/2025" (1 May, not 5 Jan), which
// is the correct locale for this site.

import { localizeFrenchDateString } from "./frenchDate";

const FR_MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const EN_MONTH_ABBR: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

interface ParsedDate {
  year: number;
  month: number; // 1-12
  day?: number; // undefined for month-only values ("10/2025")
}

function clampMonth(m: number): boolean {
  return m >= 1 && m <= 12;
}

function tryParse(value: string): ParsedDate | null {
  const v = value.trim();
  if (!v) return null;

  // ISO: YYYY-MM-DD / YYYY/MM/DD
  let m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (m) {
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (clampMonth(month) && day >= 1 && day <= 31) {
      return { year: Number(m[1]), month, day };
    }
    return null;
  }

  // ISO month-only: YYYY-MM
  m = /^(\d{4})[-/](\d{1,2})$/.exec(v);
  if (m) {
    const month = Number(m[2]);
    if (clampMonth(month)) return { year: Number(m[1]), month };
    return null;
  }

  // Day-first: DD/MM/YYYY or DD-MM-YYYY (French reading wins for ambiguity)
  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(v);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    if (clampMonth(month) && day >= 1 && day <= 31) {
      return { year: Number(m[3]), month, day };
    }
    return null;
  }

  // Month/year: MM/YYYY
  m = /^(\d{1,2})[/-](\d{4})$/.exec(v);
  if (m) {
    const month = Number(m[1]);
    if (clampMonth(month)) return { year: Number(m[2]), month };
    return null;
  }

  // "21 Nov 2025" / "21 novembre 2025" (English abbreviation or French month name)
  m = /^(\d{1,2})\s+([A-Za-zÀ-ÿ.]+)\s+(\d{4})$/.exec(v);
  if (m) {
    const day = Number(m[1]);
    const monthToken = m[2].toLowerCase().replace(/\.$/, "");
    const month =
      EN_MONTH_ABBR[monthToken.slice(0, 3)] ??
      FR_MONTHS.findIndex((name) => name.startsWith(monthToken.slice(0, 3))) + 1;
    if (month && clampMonth(month) && day >= 1 && day <= 31) {
      return { year: Number(m[3]), month, day };
    }
  }

  return null;
}

/** BCP-47 tag for a site locale ("en" reads day-first like the French layout). */
function bcp47(locale: string): string {
  const normalized = locale.trim().toLowerCase();
  return normalized === "en" ? "en-GB" : normalized;
}

/**
 * Format a free-text publication date as one consistent localized string
 * ("6 février 2026" / "6 February 2026" / "6 sánzá ya míbalé 2026", or the
 * month-year form when only the month is known). The default locale (fr) keeps
 * the hand-rolled French month table — byte-identical to the historical output;
 * other locales go through Intl with a French fallback if the runtime lacks
 * that locale's data. Returns the input unchanged when it isn't a
 * recognisable date.
 */
export function formatDisplayDate(value: string | undefined | null, locale?: string): string {
  if (!value) return "";
  const wantsLocale = (locale ?? "fr").trim().toLowerCase();
  const parsed = tryParse(value);
  // Weekday-prefixed French dates ("Vendredi 30 janvier 2026") aren't reformatted
  // on French pages (kept verbatim) but are re-expressed via Intl on other locales.
  if (!parsed) return localizeFrenchDateString(value, wantsLocale);
  if (wantsLocale && wantsLocale !== "fr") {
    try {
      const date = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day ?? 1));
      const formatter = new Intl.DateTimeFormat(
        bcp47(wantsLocale),
        parsed.day
          ? { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }
          : { month: "long", year: "numeric", timeZone: "UTC" }
      );
      return formatter.format(date);
    } catch {
      // Unknown locale on this runtime — fall through to the French rendering.
    }
  }

  const monthName = FR_MONTHS[parsed.month - 1];
  return parsed.day
    ? `${parsed.day} ${monthName} ${parsed.year}`
    : `${monthName} ${parsed.year}`;
}
